import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/universal/header";
import {
  getBulkInvoicePdf,
  useGetBulkInvoice,
  useMarkBulkInvoicePaid,
  useUpdateBulkInvoiceDueDate,
  useUpdateBulkInvoicePaymentCollector,
  useRefundBulkInvoice,
} from "@/requests/gen/react-query/bulk-invoice";
import { PaymentCollectorModal } from "./-payment-collector-modal";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RequirePermission } from "@/components/universal/require-permission";
import { DateInputModal } from "@/components/modals/date-input-modal";
import ConfirmModal from "@/components/modals/confirm-modal";
import {
  PaymentTypeEnum,
  PermissionEnum,
  TransactionDirectionEnum,
  TransactionStatusEnum,
  TransactionTypeEnum,
  type BulkInvoiceRead,
  type BulkInvoiceReadDueAt,
  type BulkInvoiceReadPaidAt,
  type Entity,
  type TransactionRead,
} from "@/requests/gen/react-query/fastAPI.schemas";
import formatUSD from "@/lib/format-currency";
import {
  addDays,
  differenceInCalendarDays,
  format,
  isPast,
  parseISO,
} from "date-fns";
import { parsePhoneNumber } from "libphonenumber-js";
import { Pencil, Printer, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Fragment, useState } from "react";
import { handlePrintPDF } from "@/lib/handle-print-pdf";

export const Route = createFileRoute(
  "/_private/_bulk-invoice/bulk-invoice/$bulkInvoiceId/",
)({
  component: BulkInvoiceDetailPage,
});

function hasRefundTransaction(transactions: TransactionRead[] | undefined) {
  return (
    transactions?.some(
      (transaction) =>
        transaction.transaction_type === TransactionTypeEnum.refund ||
        transaction.status === TransactionStatusEnum.voided,
    ) ?? false
  );
}

function paymentStatus(
  paidAt: BulkInvoiceReadPaidAt | undefined,
  dueAt: BulkInvoiceReadDueAt | undefined,
  transactions: TransactionRead[] | undefined,
) {
  if (paidAt) return { label: "Paid", variant: "success" as const };
  if (hasRefundTransaction(transactions))
    return { label: "Refunded", variant: "secondary" as const };
  if (dueAt && isPast(parseISO(dueAt)))
    return { label: "Overdue", variant: "destructive" as const };
  return { label: "Unpaid", variant: "warning" as const };
}

function formatDate(value: string) {
  return format(parseISO(value), "MMM d, yyyy");
}

function BulkInvoiceDetailPage() {
  const { bulkInvoiceId } = Route.useParams();

  const invoice = useGetBulkInvoice(bulkInvoiceId, {
    query: { enabled: !!bulkInvoiceId },
  });

  const data = invoice.data;

  return (
    <div className="text-left">
      <title>{`Invoice ${bulkInvoiceId} - Legrande Health Portal`}</title>
      <Header
        crumbs={[
          { name: "Invoices", link: { to: "/bulk-invoice/lookup" } },
          {
            name: bulkInvoiceId,
            link: {
              to: "/bulk-invoice/$bulkInvoiceId",
              params: { bulkInvoiceId },
            },
          },
        ]}
      />
      {invoice.isLoading ? (
        <p className="text-muted-foreground">Loading invoice...</p>
      ) : invoice.isError || !data ? (
        <p className="text-destructive">Failed to load invoice.</p>
      ) : (
        <Card className="mx-auto max-w-4xl gap-0 overflow-hidden p-0">
          <InvoiceMasthead data={data} refetch={invoice.refetch} />
          <AutoChargeBanner data={data} />
          <InvoiceParties data={data} refetch={invoice.refetch} />
          <InvoiceLineItems products={data.bulk_invoice_products} />
          <InvoiceTotals data={data} />
        </Card>
      )}
    </div>
  );
}

function paymentMethodLabel(data: BulkInvoiceRead) {
  if (data.payment_type === PaymentTypeEnum.card && data.payment)
    return `${data.payment.brand} ending in ${data.payment.masked_number.slice(-4)}`;
  if (data.payment_type === PaymentTypeEnum.stripe_transfer)
    return "Stripe Transfer";
  return "Other";
}

function InvoiceMasthead({
  data,
  refetch,
}: {
  data: BulkInvoiceRead;
  refetch: () => void;
}) {
  const markPaidMutation = useMarkBulkInvoicePaid();
  const updateDueDateMutation = useUpdateBulkInvoiceDueDate();
  const refundMutation = useRefundBulkInvoice();
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const status = paymentStatus(data.paid_at, data.due_at, data.transactions);

  const canRefund =
    !!data.charged_at &&
    (data.transactions?.some(
      (transaction) =>
        transaction.transaction_direction === TransactionDirectionEnum.debit,
    ) ??
      false) &&
    !hasRefundTransaction(data.transactions);

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const pdf = await getBulkInvoicePdf(data.bulk_invoice_id);
      await handlePrintPDF(pdf);
    } catch {
      toast.error("Failed to print invoice. Please try again.");
    } finally {
      setIsPrinting(false);
    }
  };

  const handleRefund = () => {
    refundMutation.mutate(
      { bulkInvoiceId: data.bulk_invoice_id },
      {
        onSuccess: () => {
          setIsRefundDialogOpen(false);
          refetch();
          toast.success("Invoice refunded.");
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.detail?.toString() ||
              "Failed to refund invoice.",
          );
        },
      },
    );
  };

  return (
    <div className="border-b px-8 py-4">
      <div className="flex justify-end mb-2">
        <Button
          variant="ghost"
          size="icon"
          className="text-foreground"
          loading={isPrinting}
          tooltip="Print invoice"
          onClick={handlePrint}
        >
          <Printer />
        </Button>
      </div>
      <div className="flex items-start justify-between gap-6">
        {data.payment_collector.logo && (
          <img
            src={data.payment_collector.logo}
            alt={data.payment_collector.name}
            className="h-11 w-auto"
          />
        )}
        <div className="ml-auto flex flex-col items-end">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Invoice
          </span>
          <div className="mt-0.5 flex items-center gap-2.5">
            <Badge variant={status.variant}>{status.label}</Badge>
            <span className="text-2xl font-bold leading-tight">
              #{data.bulk_invoice_id}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-left text-sm">
            <span className="text-muted-foreground">Issue Date</span>
            <span className="text-right font-semibold">
              {formatDate(data.created_at)}
            </span>
            <span className="text-muted-foreground">Due Date</span>
            <span className="flex items-center justify-end gap-1.5 font-semibold">
              {data.due_at ? formatDate(data.due_at) : "Pending fulfillment"}
              {!data.charged_at && (
                <RequirePermission
                  permission={PermissionEnum.administrate_legrande}
                >
                  <DateInputModal
                    title="Update Invoice Due Date"
                    trigger={
                      <Pencil className="size-3.5 cursor-pointer text-muted-foreground hover:text-primary" />
                    }
                    defaultValues={{ due_at: data.due_at || "" }}
                    fields={[
                      { name: "due_at", label: "Due At", required: true },
                    ]}
                    onSubmit={(values, close) => {
                      updateDueDateMutation.mutate(
                        {
                          bulkInvoiceId: data.bulk_invoice_id,
                          params: { due_at: values.due_at },
                        },
                        {
                          onSuccess: () => {
                            close();
                            refetch();
                            toast.success("Invoice due date updated.");
                          },
                          onError: (error) => {
                            toast.error(
                              error?.response?.data?.detail?.toString() ||
                                "Failed to update invoice due date.",
                            );
                          },
                        },
                      );
                    }}
                  />
                </RequirePermission>
              )}
            </span>
            <span className="text-muted-foreground">Payment Method</span>
            <span className="text-right font-semibold">
              {paymentMethodLabel(data)}
            </span>
            <span className="text-muted-foreground">Charged</span>
            <span className="text-right font-semibold">
              {data.charged_at ? formatDate(data.charged_at) : "Uncharged"}
            </span>
            <span className="text-muted-foreground">Paid At</span>
            <span className="flex items-center justify-end gap-1.5 font-semibold">
              {data.paid_at ? formatDate(data.paid_at) : "Unpaid"}
              <RequirePermission
                permission={PermissionEnum.administrate_legrande}
              >
                <DateInputModal
                  title="Mark Invoice Paid"
                  trigger={
                    <Pencil className="size-3.5 cursor-pointer text-muted-foreground hover:text-primary" />
                  }
                  defaultValues={{ paid_at: data.paid_at || "" }}
                  fields={[
                    { name: "paid_at", label: "Paid At", required: true },
                  ]}
                  onSubmit={async (values, close) => {
                    await markPaidMutation.mutateAsync(
                      {
                        bulkInvoiceId: data.bulk_invoice_id,
                        params: { paid_at: values.paid_at },
                      },
                      {
                        onSuccess: () => {
                          close();
                          refetch();
                          toast.success("Invoice marked as paid.");
                        },
                        onError: (error) => {
                          toast.error(
                            error?.response?.data?.detail?.toString() ||
                              "Failed to mark invoice as paid.",
                          );
                        },
                      },
                    );
                  }}
                />
              </RequirePermission>
            </span>
          </div>
          <RequirePermission permission={PermissionEnum.administrate_legrande}>
            {canRefund && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setIsRefundDialogOpen(true)}
                >
                  <Undo2 />
                  Refund
                </Button>
                <ConfirmModal
                  isOpen={isRefundDialogOpen}
                  text="Are you sure you want to refund this invoice? This cannot be undone."
                  title="Refund Invoice"
                  onCancel={() => setIsRefundDialogOpen(false)}
                  onConfirm={handleRefund}
                  img="delete"
                  cancelText="Cancel"
                  confirmText="Refund"
                  isLoading={refundMutation.isPending}
                />
              </>
            )}
          </RequirePermission>
        </div>
      </div>
    </div>
  );
}

const CHARGE_TIME_ZONE = "America/Denver";

// The UTC instant matching a wall-clock time in a named time zone (DST-aware).
function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
) {
  const guess = Date.UTC(year, month, day, hour);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CHARGE_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(guess));
  const field = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  const zoned = Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour"),
    field("minute"),
  );
  return new Date(guess - (zoned - guess));
}

// The 9 AM Mountain charge instant, as a UTC Date the caller formats in local time.
function nextChargeInstant(dueAt: string) {
  const due = parseISO(dueAt);
  const now = new Date();
  // On or before the due date, the charge runs on the due date itself.
  let day = due;
  if (differenceInCalendarDays(now, due) >= 0) {
    // Overdue: today's run has passed once it is 9 AM Mountain or later, so charge tomorrow.
    const mountainHour = Number(
      new Intl.DateTimeFormat("en-US", {
        timeZone: CHARGE_TIME_ZONE,
        hour: "numeric",
        hourCycle: "h23",
      }).format(now),
    );
    day = mountainHour >= 9 ? addDays(now, 1) : now;
  }
  return zonedTimeToUtc(day.getFullYear(), day.getMonth(), day.getDate(), 9);
}

function AutoChargeBanner({ data }: { data: BulkInvoiceRead }) {
  if (
    data.paid_at ||
    !data.due_at ||
    data.payment_type === PaymentTypeEnum.other ||
    !data.payment_type
  )
    return null;

  return (
    <div className="border-b border-yellow-300 bg-yellow-50 px-8 py-3 text-sm font-medium text-yellow-800">
      This invoice will automatically charge on{" "}
      {format(nextChargeInstant(data.due_at), "MMM d, yyyy 'at' h:mm a")}
    </div>
  );
}

function InvoiceParties({
  data,
  refetch,
}: {
  data: BulkInvoiceRead;
  refetch: () => void;
}) {
  const updatePaymentCollectorMutation = useUpdateBulkInvoicePaymentCollector();
  const canEditPaymentCollector = !data.charged_at && !data.paid_at;

  return (
    <div className="grid divide-y border-b sm:grid-cols-2 sm:divide-x sm:divide-y-0">
      <PartyBlock
        heading="Bill To"
        subtitle={
          data.account_number ? `Account #: ${data.account_number}` : undefined
        }
        party={data.ordering_entity}
      />
      <PartyBlock
        heading="Pay To"
        subtitle={data.account_number ? "\u00a0" : undefined}
        party={data.payment_collector}
        editControl={
          canEditPaymentCollector && (
            <RequirePermission
              permission={PermissionEnum.administrate_legrande}
            >
              <PaymentCollectorModal
                currentEntityId={data.payment_collector.entity_id}
                trigger={
                  <Pencil className="size-3.5 cursor-pointer text-muted-foreground hover:text-primary" />
                }
                onSubmit={(entityId, close) => {
                  updatePaymentCollectorMutation.mutate(
                    {
                      bulkInvoiceId: data.bulk_invoice_id,
                      params: { payment_collector_id: entityId },
                    },
                    {
                      onSuccess: () => {
                        close();
                        refetch();
                        toast.success("Payment collector updated.");
                      },
                      onError: (error) => {
                        toast.error(
                          error?.response?.data?.detail?.toString() ||
                            "Failed to update payment collector.",
                        );
                      },
                    },
                  );
                }}
              />
            </RequirePermission>
          )
        }
      />
    </div>
  );
}

function PartyBlock({
  heading,
  subtitle,
  party,
  editControl,
}: {
  heading: string;
  subtitle?: string;
  party: Entity;
  editControl?: React.ReactNode;
}) {
  return (
    <div className="p-7">
      <p className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
        {heading}
        {editControl}
      </p>
      <div className="mb-3">
        <p className="text-base font-bold">{party.name}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
      <div className="flex flex-col gap-2 text-sm text-foreground/80">
        <div>
          {party.st_address_1}
          {party.st_address_2 && `, ${party.st_address_2}`}
          <br />
          {party.city}, {party.state_province} {party.postal_code}
        </div>
        <div className="flex justify-between gap-3 border-t pt-2">
          <span className="text-muted-foreground">Phone</span>
          <span className="font-medium">
            {party.phone
              ? (parsePhoneNumber(party.phone)?.formatNational() ?? "-")
              : "-"}
          </span>
        </div>
        {party.email && (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Email</span>
            <span className="font-medium">{party.email}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceLineItems({
  products,
}: {
  products: BulkInvoiceRead["bulk_invoice_products"];
}) {
  const lineTaxableAmount = (
    product: BulkInvoiceRead["bulk_invoice_products"][number],
  ) => (product.unit_price ?? 0) * product.quantity - (product.discount ?? 0);

  const showDiscount =
    products.reduce((sum, product) => sum + (product.discount ?? 0), 0) > 0;
  const showTax =
    products.reduce((sum, product) => sum + (product.tax_rate ?? 0), 0) > 0;
  const columnCount = 5 + (showDiscount ? 1 : 0) + (showTax ? 2 : 0);

  return (
    <div className="overflow-x-auto px-8 pt-7 pb-2">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-2.5 pr-2 text-left font-semibold">Product</th>
            <th className="px-2 py-2.5 text-left font-semibold">Bulk Order</th>
            <th className="px-2 py-2.5 text-right font-semibold">Quantity</th>
            <th className="px-2 py-2.5 text-right font-semibold">Unit Price</th>
            {showDiscount && (
              <th className="px-2 py-2.5 text-right font-semibold">Discount</th>
            )}
            <th className="px-2 py-2.5 text-right font-semibold">
              {showDiscount ? "Taxable Amount" : "Subtotal"}
            </th>
            {showTax && (
              <>
                <th className="px-2 py-2.5 text-right font-semibold">
                  Tax Rate
                </th>
                <th className="py-2.5 pl-2 text-right font-semibold">
                  Total Tax
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const taxableAmount = lineTaxableAmount(product);
            const taxRate = product.tax_rate ?? 0;

            return (
              <Fragment key={product.bulk_invoice_product_id}>
                <tr className={product.description ? "" : "border-b"}>
                  <td className="py-3.5 pr-2 font-semibold">
                    <span className="flex items-center gap-2">
                      {product.bulk_product.display_name}
                      {product.quantity < 0 && (
                        <Badge variant="secondary">Credit</Badge>
                      )}
                    </span>
                  </td>
                  <td className="px-2 py-3.5">
                    <Link
                      to="/bulk-order/$bulkOrderId"
                      params={{ bulkOrderId: product.bulk_order_id }}
                      className="font-semibold text-primary hover:underline"
                    >
                      {product.bulk_order_id}
                    </Link>
                  </td>
                  <td className="px-2 py-3.5 text-right tabular-nums">
                    {product.quantity}
                  </td>
                  <td className="px-2 py-3.5 text-right tabular-nums">
                    {formatUSD(product.unit_price ?? 0)}
                  </td>
                  {showDiscount && (
                    <td className="px-2 py-3.5 text-right tabular-nums text-muted-foreground">
                      {formatUSD(product.discount ?? 0)}
                    </td>
                  )}
                  <td className="px-2 py-3.5 text-right tabular-nums">
                    {formatUSD(taxableAmount)}
                  </td>
                  {showTax && (
                    <>
                      <td className="px-2 py-3.5 text-right tabular-nums text-muted-foreground">
                        {taxRate}%
                      </td>
                      <td className="py-3.5 pl-2 text-right tabular-nums font-semibold">
                        {formatUSD((taxableAmount * taxRate) / 100)}
                      </td>
                    </>
                  )}
                </tr>
                {product.description && (
                  <tr className="border-b">
                    <td
                      colSpan={columnCount}
                      className="pb-3.5 pr-2 text-xs text-muted-foreground"
                    >
                      {product.description}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceTotals({ data }: { data: BulkInvoiceRead }) {
  const showDiscount = (data.discount ?? 0) > 0;
  const showSalesTax = data.sales_tax > 0;

  return (
    <div className="flex justify-end px-8 pt-5 pb-9">
      <div className="w-full max-w-xs">
        {(showDiscount || showSalesTax) && (
          <div className="border-b pb-4">
            <div className="flex justify-between py-1.5 text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">{formatUSD(data.subtotal)}</span>
            </div>
            {showDiscount && (
              <div className="flex justify-between py-1.5 text-sm">
                <span className="text-muted-foreground">Discount</span>
                <span className="tabular-nums">
                  -{formatUSD(data.discount ?? 0)}
                </span>
              </div>
            )}
            {showSalesTax && (
              <div className="flex justify-between py-1.5 text-sm">
                <span className="text-muted-foreground">Sales Tax</span>
                <span className="tabular-nums">
                  {formatUSD(data.sales_tax)}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="mt-3 flex items-baseline justify-between">
          <span className="text-sm font-semibold">Total Due</span>
          <span className="text-xl font-bold tabular-nums">
            {formatUSD(data.total)}
          </span>
        </div>
      </div>
    </div>
  );
}
